/**
 * PDP Find in Store drawer — search, geolocation, store list + stock status.
 */
import { lockBodyScroll, unlockBodyScroll } from '@theme/dialog';

(function () {
  const LOW_STOCK_THRESHOLD = 3;
  const STORES_CACHE_KEY = 'rudsak:find-in-store:stores:v2';
  const NEARBY_RADIUS_KM = 150;
  const CA_POSTAL_RE = /^[abceghjklmnprstvxy]\d[abceghjklmnprstvwxyz]\s?\d[abceghjklmnprstvwxyz]\d$/i;
  const US_ZIP_RE = /^\d{5}(?:-\d{4})?$/;
  const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const ITEM_CHEVRON =
    '<svg viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 1.5L6.5 7L1.5 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="miter"/></svg>';

  const PROVINCE_CODE = {
    alberta: 'AB',
    ab: 'AB',
    'british columbia': 'BC',
    bc: 'BC',
    manitoba: 'MB',
    mb: 'MB',
    'new brunswick': 'NB',
    nb: 'NB',
    'newfoundland and labrador': 'NL',
    newfoundland: 'NL',
    nl: 'NL',
    'northwest territories': 'NT',
    nt: 'NT',
    'nova scotia': 'NS',
    ns: 'NS',
    nunavut: 'NU',
    nu: 'NU',
    ontario: 'ON',
    on: 'ON',
    'prince edward island': 'PE',
    pe: 'PE',
    pei: 'PE',
    quebec: 'QC',
    qc: 'QC',
    saskatchewan: 'SK',
    sk: 'SK',
    yukon: 'YT',
    yt: 'YT',
  };

  /** @type {Record<string, { lat: number, lng: number }>} */
  const FSA_ORIGIN = {
    A: { lat: 47.5615, lng: -52.7126 },
    B: { lat: 44.6488, lng: -63.5752 },
    C: { lat: 46.2382, lng: -63.1311 },
    E: { lat: 45.2733, lng: -66.0633 },
    G: { lat: 46.8139, lng: -71.208 },
    H: { lat: 45.5017, lng: -73.5673 },
    J: { lat: 45.5017, lng: -73.5673 },
    K: { lat: 45.4215, lng: -75.6972 },
    L: { lat: 43.589, lng: -79.644 },
    M: { lat: 43.6532, lng: -79.3832 },
    N: { lat: 42.9849, lng: -81.2453 },
    P: { lat: 46.4917, lng: -80.993 },
    R: { lat: 49.8954, lng: -97.1385 },
    S: { lat: 52.1332, lng: -106.67 },
    T2: { lat: 51.0447, lng: -114.0719 },
    T3: { lat: 51.0447, lng: -114.0719 },
    T5: { lat: 53.5461, lng: -113.4938 },
    T6: { lat: 53.5461, lng: -113.4938 },
    V: { lat: 49.2827, lng: -123.1207 },
    X: { lat: 62.454, lng: -114.3718 },
    Y: { lat: 60.7212, lng: -135.0568 },
  };

  /** @type {Array<Record<string, any>> | null} */
  let storesCache = null;

  /**
   * @param {Element} el
   * @returns {HTMLDialogElement | null}
   */
  function getDialog(el) {
    const nested = el.closest('[data-find-in-store-dialog]');
    if (nested instanceof HTMLDialogElement) return nested;
    const controlsId =
      el.getAttribute('aria-controls') || el.closest('[aria-controls]')?.getAttribute('aria-controls');
    const byId = controlsId ? document.getElementById(controlsId) : null;
    return byId instanceof HTMLDialogElement ? byId : null;
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {boolean} expanded
   */
  function setTriggersExpanded(dialog, expanded) {
    const id = dialog.id;
    if (!id) return;
    document.querySelectorAll(`[data-find-in-store-open][aria-controls="${id}"]`).forEach((btn) => {
      if (btn instanceof HTMLElement) btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  /**
   * @param {HTMLDialogElement | null | undefined} dialog
   */
  function closeDialog(dialog) {
    if (!dialog) return;
    dialog.close?.();
    dialog.removeAttribute('open');
    setTriggersExpanded(dialog, false);
  }

  /**
   * @param {Element | null} hint
   * @param {string} text
   */
  function setHint(hint, text) {
    if (!(hint instanceof HTMLElement)) return;
    hint.textContent = text;
    hint.hidden = !text;
  }

  /**
   * @param {string} value
   */
  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * @param {string} value
   */
  function decodeHtmlEntities(value) {
    if (typeof value !== 'string' || !value.includes('&')) return value;
    const el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {string} selector
   */
  function readJsonScript(dialog, selector) {
    const script = dialog.querySelector(selector);
    try {
      return script?.textContent ? JSON.parse(script.textContent) : {};
    } catch {
      return {};
    }
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @returns {Record<string, any>}
   */
  function getPayload(dialog) {
    if (dialog._findInStorePayload) return dialog._findInStorePayload;
    dialog._findInStorePayload = readJsonScript(dialog, 'script[data-find-in-store-payload]');
    return dialog._findInStorePayload;
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @returns {Record<string, any>}
   */
  function getStrings(dialog) {
    if (dialog._findInStoreI18n) return dialog._findInStoreI18n;
    /** @type {Record<string, any>} */
    const parsed = readJsonScript(dialog, 'script[data-find-in-store-i18n]');
    /** @type {Record<string, any>} */
    const out = {};
    Object.keys(parsed).forEach((key) => {
      const value = parsed[key];
      out[key] = typeof value === 'string' ? decodeHtmlEntities(value) : value;
    });
    dialog._findInStoreI18n = out;
    return out;
  }

  /**
   * @param {number} lat1
   * @param {number} lon1
   * @param {number} lat2
   * @param {number} lon2
   */
  function haversineKm(lat1, lon1, lat2, lon2) {
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /**
   * @param {number} km
   */
  function formatDistance(km) {
    if (!Number.isFinite(km)) return '';
    return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
  }

  /**
   * @param {string} title
   */
  function formatStoreTitle(title) {
    if (!title) return '';
    return `RUDSAK — ${title.replace(/^Rudsak\s+/i, '').trim()}`;
  }

  /**
   * @param {string} value
   */
  function foldText(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  /**
   * @param {string} query
   */
  function isPostalCode(query) {
    const q = query.trim();
    return CA_POSTAL_RE.test(q) || US_ZIP_RE.test(q);
  }

  /**
   * @param {string} query
   */
  function isProvinceQuery(query) {
    const folded = foldText(query).replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
    return Boolean(folded && PROVINCE_CODE[folded]);
  }

  /**
   * @param {string} query
   */
  function compactPostal(query) {
    return query.replace(/[\s-]+/g, '').toUpperCase();
  }

  /**
   * @param {Record<string, any>} store
   */
  function formatAddressLine(store) {
    const street = String(store.address || store.address_text || '')
      .split(/<br\s*\/?>/i)[0]
      .replace(/\s+/g, ' ')
      .trim();
    const code = PROVINCE_CODE[foldText(store.province || '')] || store.province || '';
    return [street, store.city || '', code].filter(Boolean).join(', ');
  }

  /**
   * @param {Record<string, any>} store
   * @returns {{ line1: string, line2: string }}
   */
  function formatAddressBlock(store) {
    const city = String(store.city || '').trim();
    const code = PROVINCE_CODE[foldText(store.province || '')] || String(store.province || '').trim();
    const blob = String(store.address || store.address_text || '');
    const postalMatch = blob
      .toUpperCase()
      .replace(/<[^>]+>/g, ' ')
      .match(/\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/);
    let postal = '';
    if (postalMatch) {
      const compact = postalMatch[0].replace(/\s+/g, '');
      postal = `${compact.slice(0, 3)} ${compact.slice(3)}`;
    }

    const lines = String(store.address || '')
      .split(/<br\s*\/?>/i)
      .map((line) => line.replace(/,?\s*Canada\.?$/i, '').trim())
      .filter(Boolean);
    const cityFold = foldText(city);
    let street = lines.filter((line) => cityFold && !foldText(line).startsWith(cityFold)).join(', ');
    if (!street) {
      street = String(store.address_text || lines[0] || '')
        .split(',')[0]
        .trim();
    }

    const cityCode = [city, code].filter(Boolean).join(', ');
    const line2 = [cityCode, postal].filter(Boolean).join('  ');
    return { line1: street, line2 };
  }

  /**
   * @param {string} src
   * @param {number} width
   */
  function withImageWidth(src, width) {
    if (!src) return src;
    if (/([?&])width=\d+/.test(src)) return src;
    return src.includes('?') ? `${src}&width=${width}` : `${src}?width=${width}`;
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  function getVariantId(dialog) {
    const input = document.querySelector(
      'product-form-component [name="id"], form[action*="/cart/add"] [name="id"]'
    );
    if ((input instanceof HTMLInputElement || input instanceof HTMLSelectElement) && input.value) {
      return input.value;
    }
    return dialog.getAttribute('data-variant-id') || '';
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {string} storeId
   * @param {string} variantId
   * @returns {number | null}
   */
  function getStockQty(dialog, storeId, variantId) {
    const variants = getPayload(dialog).variants || {};
    const inventory = variants[variantId]?.inventory || variants[String(variantId)]?.inventory;
    if (!inventory) return null;

    let raw = inventory[storeId] ?? inventory[String(storeId)];
    if (raw == null && Array.isArray(inventory)) {
      const match = inventory.find(
        (row) =>
          row &&
          (String(row.store_id) === String(storeId) ||
            String(row.storeId) === String(storeId) ||
            String(row.id) === String(storeId))
      );
      raw = match?.quantity ?? match?.qty ?? match?.stock ?? null;
    } else if (raw && typeof raw === 'object') {
      raw = raw.quantity ?? raw.qty ?? raw.stock ?? null;
    }

    if (raw == null || raw === '') return null;
    const qty = Number(raw);
    return Number.isFinite(qty) ? qty : null;
  }

  /**
   * @param {number | null} qty
   * @param {HTMLDialogElement} dialog
   */
  function stockLabel(qty, dialog) {
    const strings = getStrings(dialog);
    if (qty == null || qty <= 0) {
      return { key: 'unavailable', label: strings.notAvailable || 'NOT AVAILABLE' };
    }
    if (qty > LOW_STOCK_THRESHOLD) {
      return { key: 'in-stock', label: strings.inStock || 'IN STOCK' };
    }
    return { key: 'limited', label: strings.limited || 'LIMITED AVAILABILITY' };
  }

  /**
   * @param {string} value
   */
  function numericStoreId(value) {
    const match = String(value || '').match(/\d+/);
    return match?.[0] ? String(Number(match[0])) : '';
  }

  /**
   * @param {string} raw
   * @returns {number | null}
   */
  function parseTimeToMinutes(raw) {
    const text = String(raw || '')
      .trim()
      .toUpperCase()
      .replace(/\./g, '')
      .replace(/\s+/g, '');
    const match = text.match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?(AM|PM)?$/);
    if (!match) return null;
    let hour = Number(match[1]);
    const minute = Number(match[2] || 0);
    const meridian = match[3] || '';
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    if (meridian === 'AM' && hour === 12) hour = 0;
    else if (meridian === 'PM' && hour !== 12) hour += 12;
    else if (!meridian && hour > 23) return null;
    return hour * 60 + minute;
  }

  /**
   * @param {number} minutes
   */
  function formatTimeLabel(minutes) {
    let hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const meridian = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${String(minute).padStart(2, '0')} ${meridian}`;
  }

  /**
   * @param {unknown} value
   * @returns {{ open: number, close: number, openLabel: string, closeLabel: string } | null}
   */
  function coerceDayRange(value) {
    if (value == null || value === '') return null;
    let openRaw = '';
    let closeRaw = '';
    if (typeof value === 'object' && !Array.isArray(value)) {
      const row = /** @type {Record<string, any>} */ (value);
      openRaw = String(row.open ?? row.start ?? row.from ?? '');
      closeRaw = String(row.close ?? row.end ?? row.to ?? '');
    } else if (Array.isArray(value) && value.length >= 2) {
      openRaw = String(value[0]);
      closeRaw = String(value[1]);
    } else {
      const range = String(value).trim();
      if (!range || /closed/i.test(range)) return null;
      const parts = range.split(/\s*[-–—]\s*/);
      if (parts.length < 2) return null;
      openRaw = parts[0] || '';
      closeRaw = parts[parts.length - 1] || '';
    }
    const open = parseTimeToMinutes(openRaw);
    const close = parseTimeToMinutes(closeRaw);
    if (open == null || close == null) return null;
    return { open, close, openLabel: formatTimeLabel(open), closeLabel: formatTimeLabel(close) };
  }

  /**
   * @param {unknown} hours
   * @returns {{ type: 'string', value: string } | { type: 'object', value: Record<string, any> } | null}
   */
  function parseHoursValue(hours) {
    if (!hours) return null;
    let normalized = hours;
    if (typeof hours === 'string') {
      const trimmed = hours.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          normalized = JSON.parse(trimmed);
        } catch {
          return { type: 'string', value: trimmed };
        }
      } else {
        return { type: 'string', value: trimmed };
      }
    }
    if (typeof normalized === 'string') return { type: 'string', value: normalized };
    if (typeof normalized === 'object' && normalized != null && !Array.isArray(normalized)) {
      return { type: 'object', value: /** @type {Record<string, any>} */ (normalized) };
    }
    return null;
  }

  /**
   * @param {unknown} hours
   * @param {string} weekday
   */
  function hoursRangeForNamedDay(hours, weekday) {
    const parsed = parseHoursValue(hours);
    if (!parsed) return null;
    if (parsed.type === 'string') return coerceDayRange(parsed.value);
    const row = parsed.value;
    const day = String(weekday || '');
    return coerceDayRange(
      row[day] ?? row[day.toLowerCase()] ?? row[day.slice(0, 3)] ?? row[day.slice(0, 3).toLowerCase()]
    );
  }

  /**
   * @param {unknown} hours
   * @param {number} [dayOffset]
   */
  function hoursRangeForDay(hours, dayOffset = 0) {
    const day = WEEKDAYS[(new Date().getDay() + dayOffset) % 7] || '';
    const parsed = parseHoursValue(hours);
    if (!parsed) return null;
    if (parsed.type === 'string') return dayOffset === 0 ? coerceDayRange(parsed.value) : null;
    return hoursRangeForNamedDay(hours, day);
  }

  /**
   * @param {unknown} hours
   */
  function nextOpenLabel(hours) {
    for (let offset = 1; offset <= 7; offset += 1) {
      const range = hoursRangeForDay(hours, offset);
      if (range) return range.openLabel;
    }
    return '';
  }

  /**
   * @param {unknown} hours
   * @param {Record<string, any>} strings
   */
  function formatHoursLine(hours, strings) {
    if (!hours) return '';
    const today = hoursRangeForDay(hours, 0);
    const closedOpens = String(strings.closedOpens || 'Closed · Opens at {{ time }}');
    const openCloses = String(strings.openCloses || 'Open · Closes {{ time }}');
    if (!today) {
      const next = nextOpenLabel(hours);
      return next ? closedOpens.replace('{{ time }}', next) : strings.closed || 'Closed';
    }
    const mins = new Date().getHours() * 60 + new Date().getMinutes();
    if (mins >= today.open && mins < today.close) {
      return openCloses.replace('{{ time }}', today.closeLabel);
    }
    const next =
      mins < today.open ? today.openLabel : nextOpenLabel(hours) || today.openLabel;
    return closedOpens.replace('{{ time }}', next);
  }

  /**
   * @param {{ open: number, close: number, openLabel: string, closeLabel: string } | null}
   * @param {Record<string, any>} strings
   */
  function formatHoursRangeLabel(range, strings) {
    if (!range) return strings.closed || 'Closed';
    return `${range.openLabel} – ${range.closeLabel}`;
  }

  /**
   * @param {unknown} hours
   * @param {Record<string, any>} strings
   * @returns {Array<{ label: string, value: string }>}
   */
  function formatHoursGroups(hours, strings) {
    const parsed = parseHoursValue(hours);
    if (!parsed || parsed.type !== 'object') return [];
    const weekdayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const allDays = [...weekdayNames, 'Saturday', 'Sunday'];
    if (!allDays.some((day) => hoursRangeForNamedDay(hours, day))) return [];
    const weekdayValues = weekdayNames.map((day) =>
      formatHoursRangeLabel(hoursRangeForNamedDay(hours, day), strings)
    );
    const groups = [];
    const first = weekdayValues[0] || '';
    const weekdaysMatch = weekdayValues.every((value) => value === first);
    if (weekdaysMatch) {
      groups.push({ label: strings.hoursMonFri || 'Mon – Fri', value: first });
    } else {
      weekdayNames.forEach((day, index) => {
        groups.push({ label: day.slice(0, 3), value: weekdayValues[index] || '' });
      });
    }
    groups.push({
      label: strings.hoursSaturday || 'Saturday',
      value: formatHoursRangeLabel(hoursRangeForNamedDay(hours, 'Saturday'), strings),
    });
    groups.push({
      label: strings.hoursSunday || 'Sunday',
      value: formatHoursRangeLabel(hoursRangeForNamedDay(hours, 'Sunday'), strings),
    });
    return groups;
  }

  /**
   * @param {Record<string, any>} store
   * @param {Array<{ handle?: string, hours?: Record<string, any> | null }> | undefined} storeHours
   */
  function withHours(store, storeHours) {
    if (!Array.isArray(storeHours) || !storeHours.length) return store;
    const id = numericStoreId(store.id);
    if (!id) return store;
    const row = storeHours.find((entry) => numericStoreId(entry.handle) === id);
    if (!row?.hours) return store;
    return { ...store, hours_of_operation: row.hours };
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  async function loadStores(dialog) {
    const payload = getPayload(dialog);
    if (!storesCache) {
      const url = payload.storesUrl;
      if (!url) return [];
      try {
        const cached = sessionStorage.getItem(STORES_CACHE_KEY);
        const parsed = cached ? JSON.parse(cached) : null;
        if (Array.isArray(parsed)) storesCache = parsed;
      } catch {
        /* ignore */
      }
      if (!storesCache) {
        const data = await (await fetch(url)).json();
        storesCache = Array.isArray(data) ? data.filter((s) => s && s.enable !== false) : [];
        try {
          sessionStorage.setItem(STORES_CACHE_KEY, JSON.stringify(storesCache));
        } catch {
          /* ignore */
        }
      }
    }
    return (storesCache || []).map((store) => withHours(store, payload.storeHours));
  }

  /**
   * @param {unknown} location
   * @returns {{ lat: number, lng: number } | null}
   */
  function coordsFromLatLng(location) {
    const lat = Number(location?.latitude ?? location?.lat);
    const lng = Number(location?.longitude ?? location?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }

  /**
   * @param {string} query
   */
  function geocodeAttempts(query) {
    const compact = compactPostal(query);
    const trimmed = query.trim();
    if (US_ZIP_RE.test(trimmed)) return [`${trimmed}, USA`, trimmed];
    if (CA_POSTAL_RE.test(trimmed)) {
      return compact.length === 6
        ? [`${compact.slice(0, 3)} ${compact.slice(3)}, Canada`, trimmed]
        : [`${trimmed}, Canada`, trimmed];
    }
    return [`${trimmed}, Canada`, trimmed];
  }

  /**
   * @param {string} query
   */
  function placesRegionCode(query) {
    return US_ZIP_RE.test(query.trim()) ? 'US' : 'CA';
  }

  /**
   * Google Places Text Search (New).
   * @param {string} query
   * @param {string} apiKey
   * @returns {Promise<{ lat: number, lng: number } | null>}
   */
  async function geocodeQuery(query, apiKey) {
    if (!apiKey) return null;
    try {
      for (const textQuery of geocodeAttempts(query)) {
        const res = await fetch('https://places.googleapis.com/v1/places:searchText', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': apiKey,
            'X-Goog-FieldMask': 'places.location',
          },
          body: JSON.stringify({
            textQuery,
            languageCode: 'en',
            regionCode: placesRegionCode(query),
            maxResultCount: 3,
          }),
        });
        if (!res.ok) continue;
        const data = await res.json();
        const places = Array.isArray(data?.places) ? data.places : [];
        for (const place of places) {
          const origin = coordsFromLatLng(place?.location);
          if (origin) return origin;
        }
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  /**
   * @param {string} query
   * @returns {{ lat: number, lng: number } | null}
   */
  function originFromFsa(query) {
    const fsa = compactPostal(query);
    if (fsa.length < 3) return null;
    return FSA_ORIGIN[fsa.slice(0, 2)] || FSA_ORIGIN[fsa[0] || ''] || null;
  }

  /**
   * @param {string} query
   * @param {string} [apiKey]
   * @returns {Promise<{ lat: number, lng: number } | null>}
   */
  async function resolveOrigin(query, apiKey) {
    if (!query || isProvinceQuery(query)) return null;
    return (await geocodeQuery(query, apiKey || '')) || (isPostalCode(query) ? originFromFsa(query) : null);
  }

  /** @type {Promise<typeof google | null> | null} */
  let googleMapsPromise = null;

  /**
   * @param {string} apiKey
   * @returns {Promise<typeof google | null>}
   */
  function loadGoogleMaps(apiKey) {
    if (!apiKey) return Promise.resolve(null);
    if (window.google?.maps) return Promise.resolve(window.google);
    if (googleMapsPromise) return googleMapsPromise;

    googleMapsPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
      script.async = true;
      script.onload = () => resolve(window.google?.maps ? window.google : null);
      script.onerror = () => resolve(null);
      document.head.appendChild(script);
    });
    return googleMapsPromise;
  }

  /**
   * @param {Element | null} container
   * @param {Array<Record<string, any>>} stores
   * @param {string} apiKey
   */
  async function renderStoresMap(container, stores, apiKey) {
    if (!(container instanceof HTMLElement)) return;
    const positions = stores
      .map((store) => ({ lat: Number(store.lat), lng: Number(store.lng), title: formatStoreTitle(store.title) }))
      .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (!positions.length || !apiKey) return;

    const google = await loadGoogleMaps(apiKey);
    if (!google) return;

    const map = new google.maps.Map(container, { disableDefaultUI: true });
    const bounds = new google.maps.LatLngBounds();

    positions.forEach((position) => {
      new google.maps.Marker({ position, map, title: position.title });
      bounds.extend(position);
    });

    if (positions.length > 1) {
      map.fitBounds(bounds);
    } else {
      map.setCenter(positions[0]);
      map.setZoom(14);
    }
  }

  /**
   * @param {Element | null} container
   * @param {Record<string, any>} store
   * @param {string} apiKey
   */
  async function renderDetailMap(container, store, apiKey) {
    if (!(container instanceof HTMLElement)) return;
    const position = { lat: Number(store.lat), lng: Number(store.lng) };
    if (!Number.isFinite(position.lat) || !Number.isFinite(position.lng) || !apiKey) return;

    const google = await loadGoogleMaps(apiKey);
    if (!google) return;

    const map = new google.maps.Map(container, { center: position, zoom: 15, disableDefaultUI: true });
    new google.maps.Marker({ position, map, title: formatStoreTitle(store.title) });
  }

  /**
   * @param {Record<string, any>} store
   * @param {{ lat?: number, lng?: number } | null} origin
   */
  function withDistance(store, origin) {
    const clone = { ...store };
    clone._distance =
      origin?.lat != null && origin?.lng != null && store.lat != null && store.lng != null
        ? haversineKm(origin.lat, origin.lng, Number(store.lat), Number(store.lng))
        : null;
    return clone;
  }

  /**
   * @param {Record<string, any>} store
   */
  function storeHaystack(store) {
    const folded = foldText(
      [store.city, store.province, store.title, store.address_text, store.address]
        .filter(Boolean)
        .join(' ')
    );
    const code = PROVINCE_CODE[foldText(store.province || '')];
    return code ? `${folded} ${code.toLowerCase()}` : folded;
  }

  /**
   * @param {Record<string, any>} store
   * @param {string[]} tokens
   */
  function storeMatchesTokens(store, tokens) {
    const hay = storeHaystack(store);
    const province = foldText(store.province || '');
    const provinceCode = foldText(PROVINCE_CODE[province] || '');
    return tokens.every((token) => {
      if (PROVINCE_CODE[token]) return province === token || provinceCode === token;
      const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(?:^|[\\s,/-])${escaped}(?:$|[\\s,/-])`).test(hay);
    });
  }

  /**
   * @param {Array<Record<string, any>>} stores
   * @param {string} query
   * @param {{ lat?: number, lng?: number } | null} origin
   */
  function filterStores(stores, query, origin) {
    let list = stores.map((store) => withDistance(store, origin));
    const tokens = foldText(query)
      .split(/[\s,]+/)
      .filter(Boolean);
    const named =
      tokens.length && !isPostalCode(query)
        ? list.filter((store) => storeMatchesTokens(store, tokens))
        : [];
    const withinRadius = (store) =>
      store._distance != null && store._distance <= NEARBY_RADIUS_KM;

    if (isProvinceQuery(query)) {
      list = named;
    } else if (named.length) {
      list = named;
    } else if (origin) {
      list = list.filter(withinRadius);
    } else if (tokens.length && !isPostalCode(query)) {
      list = [];
    }

    list.sort((a, b) => {
      const da = a._distance ?? Number.POSITIVE_INFINITY;
      const db = b._distance ?? Number.POSITIVE_INFINITY;
      if (da !== db) return da - db;
      return String(a.title || '').localeCompare(String(b.title || ''));
    });
    return list;
  }

  /**
   * @param {Record<string, any>} strings
   * @param {number} count
   * @param {string} query
   */
  function formatCountLabel(strings, count, query) {
    const template =
      count === 1
        ? strings.countOne || '{{ count }} store found near {{ query }}'
        : strings.count || '{{ count }} stores found near {{ query }}';
    return String(template)
      .replace('{{ count }}', String(count))
      .replace('{{ query }}', query.trim());
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {Array<Record<string, any>>} stores
   * @param {string} query
   */
  function renderResults(dialog, stores, query) {
    const listEl = dialog.querySelector('[data-find-in-store-list]');
    const countEl = dialog.querySelector('[data-find-in-store-count]');
    const emptyTitleEl = dialog.querySelector('[data-find-in-store-empty-title]');
    const emptyBodyEl = dialog.querySelector('[data-find-in-store-empty-body]');
    if (!(listEl instanceof HTMLElement)) return;

    const strings = getStrings(dialog);
    const variantId = getVariantId(dialog);

    if (!stores.length) {
      dialog.setAttribute('data-state', 'empty');
      dialog._findInStoreResults = [];
      dialog._findInStoreSelectedId = null;
      listEl.innerHTML = '';
      if (countEl) countEl.textContent = '';
      if (emptyTitleEl) emptyTitleEl.textContent = strings.emptyTitle || '';
      if (emptyBodyEl) {
        emptyBodyEl.textContent = String(strings.emptyBody || '').replace(
          '{{ query }}',
          String(query || '').trim() || strings.currentLocation || 'you'
        );
      }
      return;
    }

    dialog.setAttribute('data-state', 'results');
    dialog._findInStoreResults = stores;
    if (countEl) countEl.textContent = formatCountLabel(strings, stores.length, query);

    listEl.innerHTML = stores
      .map((store, index) => {
        const stock = stockLabel(getStockQty(dialog, store.id, variantId), dialog);
        const distance = formatDistance(store._distance);
        const hours = formatHoursLine(store.hours_of_operation, strings);
        const hoursHtml = hours
          ? `<p class="product-find-in-store__item-hours">${escapeHtml(hours)}</p>`
          : '';
        return `
          <li>
            <button
              type="button"
              class="product-find-in-store__item${index === 0 ? ' is-selected' : ''}"
              data-find-in-store-item
              data-store-id="${escapeHtml(store.id)}"
            >
              <span class="product-find-in-store__item-main">
                <span class="product-find-in-store__item-copy">
                  <span class="product-find-in-store__item-name">${escapeHtml(formatStoreTitle(store.title))}</span>
                  <span class="product-find-in-store__item-address">${escapeHtml(formatAddressLine(store))}</span>
                  ${hoursHtml}
                  <p class="product-find-in-store__item-status product-find-in-store__item-status--${stock.key}">${escapeHtml(stock.label)}</p>
                </span>
                <p class="product-find-in-store__item-distance">${escapeHtml(distance)}</p>
              </span>
              <span class="product-find-in-store__item-chevron" aria-hidden="true">${ITEM_CHEVRON}</span>
            </button>
          </li>
        `;
      })
      .join('');
  }

  /**
   * @param {string} phone
   * @returns {string}
   */
  function toTelHref(phone) {
    const digits = String(phone || '').replace(/\D/g, '');
    return digits ? `tel:${digits}` : '';
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {Record<string, any>} store
   */
  function renderDetail(dialog, store) {
    dialog._findInStoreSelectedId = store.id;
    dialog.setAttribute('data-state', 'detail');
    const strings = getStrings(dialog);
    const stock = stockLabel(getStockQty(dialog, store.id, getVariantId(dialog)), dialog);
    const address = formatAddressBlock(store);
    const phone = String(store.phone || '').trim();
    const telHref = toTelHref(phone);
    const nameEl = dialog.querySelector('[data-find-in-store-detail-name]');
    const statusEl = dialog.querySelector('[data-find-in-store-detail-status]');
    const addressEl = dialog.querySelector('[data-find-in-store-detail-address]');
    const phoneEl = dialog.querySelector('[data-find-in-store-detail-phone]');
    const phoneRow = dialog.querySelector('[data-find-in-store-detail-phone-row]');
    const hoursWrap = dialog.querySelector('[data-find-in-store-detail-hours]');
    const hoursRows = dialog.querySelector('[data-find-in-store-detail-hours-rows]');
    const directionsEl = dialog.querySelector('[data-find-in-store-directions]');
    const callEl = dialog.querySelector('[data-find-in-store-call]');

    if (nameEl) nameEl.textContent = formatStoreTitle(store.title);
    if (statusEl instanceof HTMLElement) {
      statusEl.className = `product-find-in-store__item-status product-find-in-store__item-status--${stock.key}`;
      statusEl.textContent = stock.label;
    }
    if (addressEl) {
      addressEl.innerHTML = `${escapeHtml(address.line1)}${
        address.line2 ? `<br>${escapeHtml(address.line2)}` : ''
      }`;
    }
    if (phoneEl) phoneEl.textContent = phone;
    if (phoneRow instanceof HTMLElement) phoneRow.hidden = !phone;

    const groups = formatHoursGroups(store.hours_of_operation, strings);
    if (hoursWrap instanceof HTMLElement) hoursWrap.hidden = !groups.length;
    if (hoursRows instanceof HTMLElement) {
      hoursRows.innerHTML = groups
        .map(
          (row) => `
            <div class="product-find-in-store__detail-hours-row">
              <span class="product-find-in-store__detail-hours-day">${escapeHtml(row.label)}</span>
              <span class="product-find-in-store__detail-hours-time">${escapeHtml(row.value)}</span>
            </div>
          `
        )
        .join('');
    }

    const detailMapContainer = dialog.querySelector('[data-find-in-store-detail-map]');
    void renderDetailMap(detailMapContainer, store, String(getPayload(dialog).placesApiKey || ''));

    if (directionsEl instanceof HTMLAnchorElement) {
      const destination =
        store.address_text ||
        [address.line1, address.line2].filter(Boolean).join(', ') ||
        (Number.isFinite(Number(store.lat)) && Number.isFinite(Number(store.lng))
          ? `${store.lat},${store.lng}`
          : '');
      directionsEl.href = destination
        ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`
        : '';
    }
    if (callEl instanceof HTMLAnchorElement) {
      callEl.href = telHref;
      callEl.toggleAttribute('hidden', !telHref);
    }

    dialog.scrollTop = 0;
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {{ lat?: number, lng?: number } | null} [origin]
   */
  async function runSearch(dialog, origin = null) {
    const input = dialog.querySelector('[data-find-in-store-input]');
    const hint = dialog.querySelector('[data-find-in-store-hint]');
    if (!(input instanceof HTMLInputElement)) return;

    const query = input.value.trim();
    const strings = getStrings(dialog);
    if (!query && !origin) {
      setHint(hint, strings.searchRequired || '');
      return;
    }

    setHint(hint, '');
    const placesApiKey = String(getPayload(dialog).placesApiKey || '');
    const searchOrigin = origin || (await resolveOrigin(query, placesApiKey));
    dialog._findInStoreOrigin = searchOrigin || null;
    renderResults(
      dialog,
      filterStores(await loadStores(dialog), query, searchOrigin),
      query || strings.currentLocation || 'you'
    );
    syncFieldState(dialog);
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  function syncFieldState(dialog) {
    const field = dialog.querySelector('[data-find-in-store-field]');
    const input = dialog.querySelector('[data-find-in-store-input]');
    if (!(field instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;
    field.classList.toggle('has-value', Boolean(input.value.trim()));
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  function resetToIdle(dialog) {
    dialog.setAttribute('data-state', 'idle');
    const listEl = dialog.querySelector('[data-find-in-store-list]');
    const countEl = dialog.querySelector('[data-find-in-store-count]');
    if (listEl) listEl.innerHTML = '';
    if (countEl) countEl.textContent = '';
    dialog._findInStoreResults = [];
    dialog._findInStoreSelectedId = null;
    dialog._findInStoreOrigin = null;
    setHint(dialog.querySelector('[data-find-in-store-hint]'), '');
  }

  /**
   * @param {number} lat
   * @param {number} lng
   * @param {string} [apiKey]
   */
  async function reverseGeocode(lat, lng, apiKey) {
    if (!apiKey) return '';
    try {
      const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
      url.searchParams.set('latlng', `${lat},${lng}`);
      url.searchParams.set('key', apiKey);
      url.searchParams.set('result_type', 'locality|postal_town|administrative_area_level_1');
      const res = await fetch(url.toString());
      if (!res.ok) return '';
      const data = await res.json();
      if (data?.status !== 'OK' || !Array.isArray(data.results) || !data.results.length) return '';
      const components = data.results[0]?.address_components || [];
      /** @param {string} type */
      const findType = (type) =>
        components.find((c) => Array.isArray(c?.types) && c.types.includes(type))?.long_name || '';
      const city = findType('locality') || findType('postal_town') || findType('sublocality');
      const region = findType('administrative_area_level_1');
      return [city, region].filter(Boolean).join(', ');
    } catch {
      return '';
    }
  }

  /**
   * @param {HTMLDialogElement} dialog
   */
  async function useCurrentLocation(dialog) {
    const locateBtn = dialog.querySelector('[data-find-in-store-locate]');
    const hint = dialog.querySelector('[data-find-in-store-hint]');
    const strings = getStrings(dialog);
    const placesApiKey = String(getPayload(dialog).placesApiKey || '');

    if (!navigator.geolocation) {
      setHint(hint, strings.locationError || '');
      return;
    }

    if (locateBtn instanceof HTMLButtonElement) locateBtn.disabled = true;
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 60000,
        });
      });
      const { latitude, longitude } = position.coords;
      const input = dialog.querySelector('[data-find-in-store-input]');
      if (input instanceof HTMLInputElement) {
        input.value =
          (await reverseGeocode(latitude, longitude, placesApiKey)) ||
          strings.currentLocation ||
          '';
      }
      syncFieldState(dialog);
      setHint(hint, '');
      await runSearch(dialog, { lat: latitude, lng: longitude });
    } catch {
      setHint(hint, strings.locationError || '');
    } finally {
      if (locateBtn instanceof HTMLButtonElement) locateBtn.disabled = false;
    }
  }

  /**
   * @param {Element | null} el
   * @param {string} value
   * @param {string} [prefix]
   */
  function setLabeledValue(el, value, prefix) {
    if (!(el instanceof HTMLElement)) return;
    el.hidden = !value;
    el.textContent = value ? `${prefix || ''}${value}` : '';
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {string} [variantId]
   */
  function syncProductSummary(dialog, variantId) {
    const id = String(variantId || getVariantId(dialog) || '');
    if (!id) return;
    dialog.setAttribute('data-variant-id', id);
    const payload = getPayload(dialog);
    const payloadVariant = payload.variants?.[id];
    const options = payloadVariant?.options || [];
    const strings = getStrings(dialog);
    const colourIndex = Number(payload.colourOptionIndex);
    const sizeIndex = Number(payload.sizeOptionIndex);
    setLabeledValue(
      dialog.querySelector('[data-find-in-store-colour]'),
      Number.isInteger(colourIndex) && colourIndex >= 0 ? String(options[colourIndex] || '') : '',
      strings.colourPrefix
    );
    setLabeledValue(
      dialog.querySelector('[data-find-in-store-size]'),
      Number.isInteger(sizeIndex) && sizeIndex >= 0 ? String(options[sizeIndex] || '') : '',
      strings.sizePrefix
    );
    const img = dialog.querySelector('[data-find-in-store-image]');
    const src = payloadVariant?.image;
    if (img instanceof HTMLImageElement && src) {
      img.src = withImageWidth(src, 240);
      img.hidden = false;
    }
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {CustomEvent} event
   */
  function onVariantUpdate(dialog, event) {
    const detail = event.detail || {};
    const variant = detail.resource || detail.variant || detail.data?.variant;
    if (!variant?.id) return;
    syncProductSummary(dialog, String(variant.id));
    const state = dialog.getAttribute('data-state');
    if (state === 'detail') {
      const selectedId = dialog._findInStoreSelectedId;
      const selected = (dialog._findInStoreResults || []).find(
        (store) => String(store.id) === String(selectedId)
      );
      if (selected) renderDetail(dialog, selected);
      return;
    }
    if (state !== 'results' && state !== 'empty') return;
    const input = dialog.querySelector('[data-find-in-store-input]');
    const query = input instanceof HTMLInputElement ? input.value : '';
    const origin = dialog._findInStoreOrigin || null;
    loadStores(dialog).then((stores) => {
      renderResults(dialog, filterStores(stores, query, origin), query || 'you');
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openBtn = target.closest('[data-find-in-store-open]');
    if (openBtn instanceof HTMLElement) {
      event.preventDefault();
      const dialog = getDialog(openBtn);
      if (!dialog) return;
      lockBodyScroll(window.scrollY);
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      setTriggersExpanded(dialog, true);
      syncProductSummary(dialog);
      syncFieldState(dialog);
      const idleMapContainer = dialog.querySelector('[data-find-in-store-map]');
      const idleApiKey = String(getPayload(dialog).placesApiKey || '');
      loadStores(dialog).then((stores) => renderStoresMap(idleMapContainer, stores, idleApiKey));
      return;
    }

    const closeBtn = target.closest('[data-find-in-store-close]');
    if (closeBtn) {
      closeDialog(getDialog(closeBtn));
      return;
    }

    const clearBtn = target.closest('[data-find-in-store-clear]');
    if (clearBtn) {
      const dialog = getDialog(clearBtn);
      const input = dialog?.querySelector('[data-find-in-store-input]');
      if (dialog && input instanceof HTMLInputElement) {
        input.value = '';
        resetToIdle(dialog);
        syncFieldState(dialog);
        input.focus();
      }
      return;
    }

    const locateBtn = target.closest('[data-find-in-store-locate]');
    if (locateBtn) {
      const dialog = getDialog(locateBtn);
      if (dialog) void useCurrentLocation(dialog);
      return;
    }

    const submitBtn = target.closest('[data-find-in-store-submit]');
    if (submitBtn) {
      const dialog = getDialog(submitBtn);
      if (dialog) void runSearch(dialog);
      return;
    }

    const orderOnline = target.closest('[data-find-in-store-order-online]');
    if (orderOnline) {
      closeDialog(getDialog(orderOnline));
      return;
    }

    const backBtn = target.closest('[data-find-in-store-back]');
    if (backBtn) {
      const dialog = getDialog(backBtn);
      if (dialog) dialog.setAttribute('data-state', 'results');
      return;
    }

    const itemBtn = target.closest('[data-find-in-store-item]');
    if (itemBtn instanceof HTMLElement) {
      const dialog = getDialog(itemBtn);
      const storeId = itemBtn.getAttribute('data-store-id');
      const store = (dialog?._findInStoreResults || []).find(
        (row) => String(row.id) === String(storeId)
      );
      if (dialog && store) renderDetail(dialog, store);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-find-in-store-input]')) return;
    event.preventDefault();
    const dialog = getDialog(target);
    if (dialog) void runSearch(dialog);
  });

  document.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-find-in-store-input]')) return;
    const dialog = getDialog(target);
    if (!dialog) return;
    syncFieldState(dialog);
    if (!target.value.trim() && dialog.getAttribute('data-state') !== 'idle') resetToIdle(dialog);
  });

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('[data-find-in-store-dialog]')) return;
      setTriggersExpanded(dialog, false);
      unlockBodyScroll();
    },
    { capture: true }
  );

  document.addEventListener('click', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('[data-find-in-store-dialog]')) return;
    const rect = dialog.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) closeDialog(dialog);
  });

  document.addEventListener('variant:update', (event) => {
    document.querySelectorAll('[data-find-in-store-dialog]').forEach((dialog) => {
      if (dialog instanceof HTMLDialogElement) {
        onVariantUpdate(dialog, /** @type {CustomEvent} */ (event));
      }
    });
  });
})();
